import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import VideoPlayer from 'yoda-site-components/lib/components/VideoPlayer/VideoPlayer';
import { bindActionCreators } from 'redux';
import classNames from 'classnames/bind';
import S7Image from 'yoda-core-components/lib/components/S7Image/S7Image';
import { selectDeviceType } from 'yoda-site-components/lib/selectors/ContextSelector';
import SlickCarousel from 'yoda-site-components/lib/components/SlickCarousel/SlickCarousel';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { pdpToolClickAnalytics, videoPlayerAnalytics } from '../../../actions/AnalyticsAction';
import * as styles from './BundleChildImage.css';
import VideoContainer from '../../VideoContainer/VideoContainer';
import formattedFilmStripData from '../../../helpers/FormatImagesAndVideos';
import getProductImagesAndVideos from '../../../selectors/Bundle/BundleImageSelector';

const cx = classNames.bind(styles);
const carouselTheme = {
    carouselSelected: cx(styles.selected),
    carouselBullets: `${cx(styles.carouselBullets)} ${dt(['bg-white', 'mt-2', 'text-[0]'])}`,
    bullet: styles.bullet,
    slideClass: `${cx(styles.slide)} ${dt(['opacity-100', 'w-full', 'h-full'])}`,
};
const SCENE7 = 'Scene7';
const scene7VideoContainerStyle = dt([
    'w-full',
    'lg:h-[285px]',
    'mdOnly:h-[359px]',
    'smOnly:h-[220px]',
    'smOnly:w-[90vw]',
]);
const videoJsStyle = dt(['h-full', 'mdOnly:w-full']);
const unplayableVideoStyle = dt(['lg:h-[285px]', 'mdOnly:h-[359px]', 'smOnly:h-[220px]']);
export class BundleChildImage extends Component {
    static defaultProps = {
        actions: {},
        productImagesAndVideos: {},
        enableVideoPlacementChange: false,
    };

    static propTypes = {
        productImagesAndVideos: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        deviceType: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
        enableVideoPlacementChange: PropTypes.bool,
        actions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    };

    constructor() {
        super();
        this.state = {
            shouldAnalyticsTrigger: true,
            shouldScene7Render: false,
        };
        this.productImageListRenderer = this.productImageListRenderer.bind(this);
        this.slideCallback = this.slideCallback.bind(this);
    }

    shouldComponentUpdate(nextProps, nextState) {
        /* istanbul ignore next */
        if (
            JSON.stringify(nextProps.productImagesAndVideos) !==
                JSON.stringify(this.props.productImagesAndVideos) ||
            this.state.shouldScene7Render !== nextState.shouldScene7Render
        ) {
            return true;
        }
        return false;
    }

    loadScene7 = (idx) => {
        if (!this.state.shouldScene7Render) {
            this.setState({
                shouldScene7Render: true,
            });
        }

        /** If already loaded */
        if (window.videojs) {
            this.loadScene7Success(`pdpVideo-${idx}`);
        }
    };

    loadScene7Success = (pageId) => {
        const videoPlayerData = window.videojs(pageId);
        this.props.actions.videoPlayerAnalytics(videoPlayerData);
    };

    slideCallback = (idx) => {
        const currentObject = get(this.props, `productImagesAndVideos.productChildItems[${idx}]`);

        if (currentObject && currentObject.isVideo && currentObject.type === SCENE7) {
            this.loadScene7(idx);
        }

        this.handleAnalytics();
    };

    handleAnalytics() {
        if (this.state.shouldAnalyticsTrigger) {
            this.props.actions.pdpToolClickAnalytics('alternate images');
        }
        this.setState({ shouldAnalyticsTrigger: true });
    }

    /* istanbul ignore next */
    productImageListRenderer(dataItem, idx) {
        if (dataItem.isVideo) {
            /* istanbul ignore else */
            if (dataItem.type === SCENE7) {
                return this.renderScene7Video(dataItem, idx);
            }
            return (
                <div className={`${cx('unplayable-video')} ${unplayableVideoStyle}`}>
                    <VideoContainer url={dataItem.url} />
                </div>
            );
        }

        return this.renderImage(dataItem, idx);
    }

    /* istanbul ignore next */
    renderScene7Video(dataItem, idx) {
        return (
            <div className={`${cx('scene7-video-container')} ${scene7VideoContainerStyle}`}>
                <VideoPlayer
                    url={dataItem.url}
                    index={`${this.props.index}-${idx}`}
                    pageId={`pdpVideo-${idx}`}
                    videoTheme={`${cx('videoJs')} ${videoJsStyle}`}
                    playerProps={{ url: dataItem.id }}
                    automationId={`bundleChild-video-${idx}`}
                    triggerAnalytics={this.loadScene7Success}
                    disableAutoPlay
                    isPDP
                />
            </div>
        );
    }

    /* istanbul ignore next */
    renderImage(dataItem, idx) {
        const automationId = 'bundle-product';
        return (
            <S7Image
                src={dataItem.url}
                alt={dataItem.altText}
                recipe="$product_pdp$"
                automationId={`${automationId}-s7image-${idx}`}
                context="pdp"
            />
        );
    }

    renderProductImage(dataItem) {
        const {
            deviceType,
            enableVideoPlacementChange,
            preferences: { videoPlacementPosition = 3 } = {},
        } = this.props;
        let theme = carouselTheme;
        /* istanbul ignore if */
        if (dataItem.length <= 1) {
            theme = {
                ...carouselTheme,
                slideClass: `${cx(styles.singleSlide)} ${dt([
                    'opacity-100',
                    'smOnly:w-[330px]',
                    'smOnly:m-auto',
                ])}`,
            };
        }
        return (
            <SlickCarousel
                carouselData={
                    enableVideoPlacementChange
                        ? formattedFilmStripData(videoPlacementPosition, dataItem)
                        : dataItem
                }
                carouselItemRenderer={this.productImageListRenderer}
                themeConfig={theme}
                automationId="bundle-image"
                rewindOnChange
                displayArrows={!deviceType.isMobile}
                repeat={false}
                autoSize={!deviceType.isMobile}
                slideCallback={this.slideCallback}
                updateRender={this.state.shouldScene7Render}
                isBundle
                isPDPImage
                enableVideoPlacementChange={enableVideoPlacementChange}
            />
        );
    }

    render() {
        const { productImagesAndVideos } = this.props;
        const imagesAndVideos =
            productImagesAndVideos &&
            productImagesAndVideos.productChildItems &&
            productImagesAndVideos.productChildItems.length
                ? productImagesAndVideos.productChildItems
                : [];
        return (
            <section>
                {imagesAndVideos && imagesAndVideos.length
                    ? this.renderProductImage(imagesAndVideos)
                    : null}
            </section>
        );
    }
}

const mapStateToProps = (state, ownProps) => ({
    productImagesAndVideos: getProductImagesAndVideos(state, ownProps.productDetails.id),
    enableVideoPlacementChange: get(state, 'context.featureFlags.enableVideoPlacementChange', true),
    deviceType: selectDeviceType(state),
});
/* istanbul ignore next */
const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators({ pdpToolClickAnalytics, videoPlayerAnalytics }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(BundleChildImage);
